import { SUPPORTED_LOCALES, isLocale, type Locale } from './i18n';

/**
 * A viewer's preference for automatic translation of user-generated content.
 *
 * Both fields are optional and nullable on purpose: a GraphQL document may not
 * select them, and a row written before the columns existed may not carry them.
 * Every absent value has to read as "translate", so no surface can silently
 * turn translation off by forgetting to ask for the preference.
 */
export interface TranslationPreferences {
  autoTranslateEnabled?: boolean | null;
  autoTranslateExcludedLocales?: readonly string[] | null;
}

/** What every viewer gets until they change something — today's behaviour. */
export const DEFAULT_TRANSLATION_PREFERENCES: TranslationPreferences = {
  autoTranslateEnabled: true,
  autoTranslateExcludedLocales: [],
};

/**
 * Should content originally written in `sourceLocale` be auto-translated for
 * this viewer? The global switch is checked first, then the per-language
 * blocklist — so turning auto-translate off wins over any stored exception.
 */
export function shouldAutoTranslate(sourceLocale: string, preferences?: TranslationPreferences | null): boolean {
  if (!preferences) {
    return true;
  }

  if (preferences.autoTranslateEnabled === false) {
    return false;
  }

  const excluded = preferences.autoTranslateExcludedLocales;
  if (!excluded) {
    return true;
  }

  return !excluded.includes(sourceLocale);
}

/**
 * Canonicalize an exclusion list from anywhere — a JSONB column or a client
 * payload — into supported locales only. Ordering follows SUPPORTED_LOCALES so
 * the stored value never churns just because the user clicked in a different
 * order, which also makes the list cheap to compare.
 */
export function normalizeExcludedLocales(value: unknown): Locale[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const selected = new Set(value.filter((entry): entry is Locale => typeof entry === 'string' && isLocale(entry)));

  return SUPPORTED_LOCALES.filter((locale) => selected.has(locale));
}
